import {ActionIcon, Button, createStyles, Group, Loader, Popover, Stack, Text, Textarea} from '@mantine/core';
import React, {useState} from 'react';

import * as NavConstants from 'constants/NavConstants';
import {openExistingChat} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {runAgentDirectly} from 'llm/routines/RoutinesDAO';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {getUserMessage} from 'util/Net';

interface AgentChatButtonProps {
  agentName: string;
  routineId: string;
}

// PopoverDropdown always sets its own inline `width` style, rem()-converted assuming a 16px root
// (falling back to "max-content" if no `width` prop is given). This app's root font-size is 62.5%
// (10px, see e.g. QuickSearchInput.tsx), so a plain `width` prop renders at 62.5% of the intended
// px value. `!important` in a class beats that inline style regardless of its value, so pin the
// dropdown to a true px width here instead of passing a `width` prop.
const useStyles = createStyles(() => ({
  dropdown: {
    width: '320px !important',
  },
}));

/**
 * Per-agent "Chat" action for the factory agent roster: an inline popover with a single-message
 * textarea. Sending calls the run-with-message endpoint for the agent (routine) and, on success,
 * opens the new chat session, always in the semi-expanded sidepanel (not full-screen, which would
 * pull focus away from the factory page it was started from). A failed send keeps the typed
 * message in place so it is not lost.
 */
export default function AgentChatButton({agentName, routineId}: AgentChatButtonProps): JSX.Element {
  const {classes} = useStyles();
  const [opened, setOpened] = useState(false);
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSend = message.trim() !== '' && !isSending;

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    // Enter (without shift) sends, matching the main chat input; shift+Enter still inserts a
    // newline.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleSend = async (): Promise<void> => {
    if (!canSend) {
      return;
    }
    setIsSending(true);
    setError(null);
    try {
      const {chatSessionId} = await runAgentDirectly(routineId, message.trim());
      setMessage('');
      setOpened(false);
      openExistingChat(chatSessionId);
      // Always semi-expanded (not expandSection's full-screen), so the factory page stays visible.
      SidepanelActions.unexpandToSection(NavConstants.SECTIONS.AI);
    } catch (e) {
      const userMessage = getUserMessage(e);
      // Re-throw unexpected errors (those without a user-facing message) rather than swallowing
      // them, matching the error handling in useRoutineMutations.
      if (userMessage == null) {
        throw e;
      }
      setError(userMessage);
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Popover
      opened={opened}
      onClose={() => {
        if (!isSending) {
          // Closing without sending (e.g. clicking outside) discards the draft, so the next open
          // starts fresh rather than resurfacing an abandoned message.
          setOpened(false);
          setMessage('');
          setError(null);
        }
      }}
      position="bottom-end"
      withArrow={true}
      shadow="md"
    >
      <Popover.Target>
        <Button
          variant="ghost"
          size="xs"
          leftIcon={<BenchlingIcon icon="sparkle" />}
          aria-label={`Chat with ${agentName}`}
          onClick={() => {
            setOpened((prevOpened) => !prevOpened);
          }}
        >
          Chat
        </Button>
      </Popover.Target>
      <Popover.Dropdown className={classes.dropdown}>
        <Stack spacing="xs">
          <Textarea
            aria-label={`Message to ${agentName}`}
            autoFocus={true}
            autosize={true}
            minRows={3}
            maxRows={10}
            placeholder={`Message ${agentName}...`}
            value={message}
            disabled={isSending}
            onChange={(event) => {
              setMessage(event.currentTarget.value);
              setError(null);
            }}
            onKeyDown={handleKeyDown}
          />
          {error != null && (
            <Text size="xs" c="red">
              {error}
            </Text>
          )}
          <Group position="right" spacing="xs" noWrap={true}>
            {isSending ? (
              <Loader size="xs" />
            ) : (
              <ActionIcon
                aria-label="Send"
                color="blue"
                variant="filled"
                radius="xl"
                disabled={!canSend}
                onClick={handleSend}
              >
                <BenchlingIcon icon="arrow-up" />
              </ActionIcon>
            )}
          </Group>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
